import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE } from "const"
import { CcRequirementPriority, CcRequirementPriorityCreate } from "server/models"
import { useInput, StateType } from "client/utils"
import {
    StoreType,
    CcRequirementPriorityValidation,
    SaveCcRequirementPriority,
    closeModal,
    setCcRequirementPriorityEdit,
    setCcRequirementPriorityReloadList,
    setCcRequirementPriorityReloadEdit,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    SelectDropdown,
    SelectDropdownItem,
    Feedback,
    Button,
    SpinnerIcon,
} from "client/components"

interface PriorityEditProps {
    priority: CcRequirementPriority
    editAccess: boolean
}

let isMounted: boolean

const Form = ({
    priority,
    editAccess,
}: PriorityEditProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(priority.name)
    const [description, setDescription] = useInput(priority.description)
    const [isDefault, setDefault] = useState(priority.default)
    const [validation, setValidation] = useState<CcRequirementPriorityValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (modals.length && isMounted) dispatch(closeModal())
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newPriorityData: CcRequirementPriorityCreate = {
            accountId: priority.accountId || userData.accountId,
            projectId: priority.projectId || project.id,
            name,
            description,
            creator: priority.id ? priority.creator : userData.userId,
            default: isDefault,
            order: priority.id ? priority.order : 0,
        }

        SaveCcRequirementPriority(priority.id, newPriorityData, lang)
        .then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)
            }
            if (res.validation) return

            dispatch(setCcRequirementPriorityEdit({
                ...newPriorityData,
                id: res.newPriorityId || priority.id,
                order: res.newPriorityOrder || priority.order,
            }))

            dispatch(setCcRequirementPriorityReloadList(false))
            setTimeout(() => {
                dispatch(setCcRequirementPriorityReloadList(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setCcRequirementPriorityReloadEdit(false))
                setTimeout(() => {
                    dispatch(setCcRequirementPriorityReloadEdit(true))
                }, 0)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} readOnly={!editAccess} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <Textarea value={description} onChange={setDescription} readOnly={!editAccess} />
                        <Feedback>{validation && validation.description}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.default}</Label>
                        {
                            editAccess && (
                                <SelectDropdown title={isDefault ? lang.yes : lang.no}>
                                    <SelectDropdownItem onClick={() => setDefault(true)}>{lang.yes}</SelectDropdownItem>
                                    <SelectDropdownItem onClick={() => setDefault(false)}>{lang.no}</SelectDropdownItem>
                                </SelectDropdown>
                            )
                        }
                        {
                            !editAccess && (
                                <Input type="text" value={isDefault ? lang.yes : lang.no} readOnly />
                            )
                        }
                        <Feedback>{validation && validation.default}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    {
                        editAccess && [
                            <Button key="save" variant="success" row onClick={() => onSave(1)}>
                                {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                            </Button>,
                            <Button key="apply" variant="success" row onClick={() => onSave(2)}>
                                {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                            </Button>,
                        ]
                    }
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
